import type { HotkeyScope } from "../types";
import { ARIA_ATTRS, EVENTS } from "../types/defaults";
import { EventEmitter } from "./EventEmitter";

export class ScopeManager extends EventEmitter {
  private scopes: Map<string, HotkeyScope>;
  private activeScopes: Set<string>;
  private focusListeners: Map<string, () => void>;
  private scopeStack: string[];

  private activeScopesCache: HotkeyScope[] | null = null;
  private highestPriorityScopeCache: HotkeyScope | null = null;
  private allScopesCache: HotkeyScope[] | null = null;
  private scopeVersion: number = 0;

  constructor() {
    super();
    this.scopes = new Map();
    this.activeScopes = new Set();
    this.focusListeners = new Map();
    this.scopeStack = [];
  }

  private invalidateCaches(): void {
    this.scopeVersion++;
    this.activeScopesCache = null;
    this.highestPriorityScopeCache = null;
    this.allScopesCache = null;
  }

  registerScope(id: string, name: string, element: HTMLElement | null = null, priority: number = 0): HotkeyScope {
    const scope: HotkeyScope = {
      id,
      name,
      element,
      activeHotkeys: new Set(),
      priority,
      active: false,
    };

    this.scopes.set(id, scope);

    if (element) {
      this.setupFocusListeners(scope);
    }

    this.invalidateCaches();

    return scope;
  }

  unregisterScope(id: string): boolean {
    const scope = this.scopes.get(id);
    if (!scope) return false;

    if (scope.element) {
      this.cleanupFocusListeners(scope);
    }

    if (scope.active) {
      this.deactivateScope(id);
    }

    this.scopes.delete(id);

    this.invalidateCaches();

    return true;
  }

  activateScope(id: string): boolean {
    const scope = this.scopes.get(id);
    if (!scope) return false;

    if (scope.active) return true;

    for (const activeId of this.activeScopes) {
      const activeScope = this.scopes.get(activeId);
      if (activeScope && activeScope.priority < scope.priority) {
        this.deactivateScope(activeId);
      }
    }

    scope.active = true;
    this.activeScopes.add(id);

    if (scope.element) {
      scope.element.classList.add("crx-hotkey-scope-active");
      scope.element.setAttribute(ARIA_ATTRS.HOTKEY_SCOPE, id);
    }

    this.invalidateCaches();

    this.emit(EVENTS.SCOPE_ACTIVATED, scope);

    return true;
  }

  deactivateScope(id: string): boolean {
    const scope = this.scopes.get(id);
    if (!scope) return false;

    if (!scope.active) return true;

    scope.active = false;
    this.activeScopes.delete(id);

    if (scope.element) {
      scope.element.classList.remove("crx-hotkey-scope-active");
      scope.element.removeAttribute(ARIA_ATTRS.HOTKEY_SCOPE);
    }

    this.invalidateCaches();

    this.emit(EVENTS.SCOPE_DEACTIVATED, scope);

    return true;
  }

  toggleScope(id: string): boolean {
    const scope = this.scopes.get(id);
    if (!scope) return false;

    if (scope.active) {
      return this.deactivateScope(id);
    } else {
      return this.activateScope(id);
    }
  }

  /**
   * Push a scope onto the stack and activate it.
   * Use this for nested contexts like modals-within-modals or edit-mode-within-view.
   * The previous scope remains in the stack and will be restored when popScope is called.
   */
  pushScope(id: string): boolean {
    const scope = this.scopes.get(id);
    if (!scope) return false;

    // Add to stack (even if already active, to track nesting)
    this.scopeStack.push(id);

    // Activate the scope
    return this.activateScope(id);
  }

  /**
   * Pop the current scope from the stack and deactivate it.
   * If there's a previous scope in the stack, it will remain active.
   * @returns The popped scope id, or undefined if the stack was empty
   */
  popScope(): string | undefined {
    const poppedId = this.scopeStack.pop();
    if (!poppedId) return undefined;

    // Deactivate the popped scope
    this.deactivateScope(poppedId);

    return poppedId;
  }

  /**
   * Get the current scope stack (for debugging/introspection).
   * @returns A copy of the scope stack array
   */
  getScopeStack(): string[] {
    return [...this.scopeStack];
  }

  /**
   * Get the current (topmost) scope from the stack.
   * @returns The current scope id, or undefined if the stack is empty
   */
  getCurrentStackScope(): string | undefined {
    return this.scopeStack[this.scopeStack.length - 1];
  }

  getActiveScopes(): HotkeyScope[] {
    if (this.activeScopesCache !== null) {
      return this.activeScopesCache;
    }

    const scopes = Array.from(this.activeScopes)
      .map((id) => this.scopes.get(id)!)
      .filter(Boolean);

    scopes.sort((a, b) => b.priority - a.priority);

    this.activeScopesCache = scopes;
    return scopes;
  }

  getHighestPriorityScope(): HotkeyScope | null {
    if (this.highestPriorityScopeCache !== null) {
      return this.highestPriorityScopeCache;
    }

    const activeScopes = this.getActiveScopes();
    const result = activeScopes.length > 0 ? activeScopes[0] : null;

    this.highestPriorityScopeCache = result;
    return result;
  }

  isScopeActive(id: string): boolean {
    const scope = this.scopes.get(id);
    return scope?.active || false;
  }

  getScope(id: string): HotkeyScope | undefined {
    return this.scopes.get(id);
  }

  getAllScopes(): HotkeyScope[] {
    if (this.allScopesCache !== null) {
      return this.allScopesCache;
    }

    const result = Array.from(this.scopes.values());

    this.allScopesCache = result;
    return result;
  }

  addHotkeyToScope(scopeId: string, hotkeyId: string): boolean {
    const scope = this.scopes.get(scopeId);
    if (!scope) return false;

    scope.activeHotkeys.add(hotkeyId);
    return true;
  }

  removeHotkeyFromScope(scopeId: string, hotkeyId: string): boolean {
    const scope = this.scopes.get(scopeId);
    if (!scope) return false;

    scope.activeHotkeys.delete(hotkeyId);
    return true;
  }

  getHotkeysInScope(scopeId: string): Set<string> {
    const scope = this.scopes.get(scopeId);
    return scope?.activeHotkeys || new Set();
  }

  private setupFocusListeners(scope: HotkeyScope): void {
    if (!scope.element) return;

    const handleFocus = () => {
      this.activateScope(scope.id);
    };

    const handleBlur = () => {
      setTimeout(() => {
        if (scope.element && !scope.element.contains(document.activeElement)) {
          this.deactivateScope(scope.id);
        }
      }, 10);
    };

    scope.element.addEventListener("focusin", handleFocus);
    scope.element.addEventListener("focusout", handleBlur);

    const cleanup = () => {
      scope.element?.removeEventListener("focusin", handleFocus);
      scope.element?.removeEventListener("focusout", handleBlur);
    };

    this.focusListeners.set(scope.id, cleanup);
  }

  private cleanupFocusListeners(scope: HotkeyScope): void {
    const cleanup = this.focusListeners.get(scope.id);
    if (cleanup) {
      cleanup();
      this.focusListeners.delete(scope.id);
    }
  }

  static makeFocusable(element: HTMLElement): void {
    if (!element.hasAttribute("tabindex")) {
      element.setAttribute("tabindex", "0");
    }

    if (!element.hasAttribute("role")) {
      element.setAttribute("role", "region");
    }

    if (!element.hasAttribute("aria-label") && !element.hasAttribute("aria-labelledby")) {
      console.warn("Scope element should have aria-label or aria-labelledby:", element);
    }
  }

  destroy(): void {
    for (const scope of this.scopes.values()) {
      if (scope.element) {
        this.cleanupFocusListeners(scope);
      }
      if (scope.active) {
        this.deactivateScope(scope.id);
      }
    }

    this.scopes.clear();
    this.activeScopes.clear();
    this.focusListeners.clear();
    this.scopeStack = [];
    this.invalidateCaches();
    this.removeAllListeners();
  }

  clearCaches(): void {
    this.invalidateCaches();
  }

  getCacheStats(): {
    scopeVersion: number;
    activeScopesCached: boolean;
    highestPriorityCached: boolean;
    allScopesCached: boolean;
    totalScopes: number;
    activeScopes: number;
    scopeStackDepth: number;
  } {
    return {
      scopeVersion: this.scopeVersion,
      activeScopesCached: this.activeScopesCache !== null,
      highestPriorityCached: this.highestPriorityScopeCache !== undefined,
      allScopesCached: this.allScopesCache !== null,
      totalScopes: this.scopes.size,
      activeScopes: this.activeScopes.size,
      scopeStackDepth: this.scopeStack.length,
    };
  }
}
